// src/utils/transformGrid.ts
//
// Single source of truth for turning a raw `grids` database row into the
// StaffAllocationGrid shape the app uses. Previously three contexts each had
// their own copy of this logic (GridContext/gridsService, MultiGridContext,
// StabilizedContexts) with subtly different defaults and field names — a subject
// could get different lines/study-periods depending on which context read it.
//
// This canonical transform reproduces MultiGridContext's logic (the transform
// that drives the Timetable Editor) so that critical path is unchanged, while the
// other contexts adopt it — which also fixes their latent bugs (e.g. reading a
// non-existent `year` column instead of `academic_year`). Study-period objects
// carry BOTH `lineIds` and `lines` populated identically, so any consumer that
// reads either field keeps working.

import { StaffAllocationGrid, LineDefinition, StudyPeriodDefinition } from '../types/grid';

/** Determine the study period a line belongs to, from its id/name. */
export function determineStudyPeriod(lineId: string): string {
  if (!lineId) return 'semester1';
  const id = (lineId || '').toLowerCase();

  if (id.includes('s1') || id.includes('sem1') || id.includes('semester1')) return 'semester1';
  if (id.includes('s2') || id.includes('sem2') || id.includes('semester2')) return 'semester2';

  if (['a', 'b', 'c', 'd', 'e', 'f'].includes(id)) return 'semester1';
  if (['g', 'h', 'i', 'j', 'k', 'l'].includes(id)) return 'semester2';

  if (id.endsWith('1') && !id.includes('s2')) return 'semester1';
  if (id.endsWith('2') && !id.includes('s1')) return 'semester2';

  return 'semester1';
}

/**
 * Collect every line id the classes actually occupy.
 *
 * A grid can carry classes without ever storing line definitions — the 2027 Main
 * Grid is exactly this shape (`grid_data` holds only `classes`, `settings` is
 * `{}`). Falling back to a fixed A–F list in that case renders fewer lines than
 * the grid uses and silently hides every class on the missing lines: 31 of the 68
 * Arts classes sat on G–L with nowhere to display. Deriving the list from the
 * classes means a grid can never render fewer lines than its own data occupies.
 *
 * Reads `allowedLineIds` and the keys of `lineAssignments`, since a class can be
 * assigned to a line that was never listed as allowed.
 */
function deriveLinesFromClasses(classes: any[]): string[] {
  const ids = new Set<string>();

  for (const cls of classes) {
    if (!cls || typeof cls !== 'object') continue;

    if (Array.isArray(cls.allowedLineIds)) {
      for (const id of cls.allowedLineIds) {
        if (typeof id === 'string' && id.trim()) ids.add(id.trim());
      }
    }

    if (cls.lineAssignments && typeof cls.lineAssignments === 'object') {
      for (const id of Object.keys(cls.lineAssignments)) {
        if (id.trim()) ids.add(id.trim());
      }
    }
  }

  // Shortest-then-alphabetical keeps the single-letter timetable lines (A…L) in
  // order and ahead of the named blocks (XAM, XPM, YAM, YPM).
  return [...ids].sort((a, b) => a.length - b.length || a.localeCompare(b));
}

/** Normalise raw line data (string ids or objects) to LineDefinition objects. */
function normaliseLines(
  gridData: any,
  settings: any,
  metadata: any,
  classes: any[] = []
): LineDefinition[] {
  const derived = deriveLinesFromClasses(classes);

  const rawLines =
    gridData.lines ||
    settings.lines ||
    metadata.lines ||
    (derived.length > 0 ? derived : ['A', 'B', 'C', 'D', 'E', 'F']);

  if (!Array.isArray(rawLines) || rawLines.length === 0) return [];

  if (typeof rawLines[0] === 'object') {
    return rawLines.map((lineObj: any) => ({
      id: lineObj.id || lineObj.name || 'unknown',
      name: lineObj.name || `Line ${lineObj.id || 'unknown'}`,
      studyPeriod: lineObj.studyPeriod || determineStudyPeriod(lineObj.id || lineObj.name || 'unknown'),
      pairedLineId: lineObj.pairedLineId,
      days: lineObj.days,
    }));
  }

  return rawLines.map((lineId: any) => {
    const id = typeof lineId === 'string' ? lineId : String(lineId);
    return {
      id,
      name: `Line ${id}`,
      studyPeriod: determineStudyPeriod(id),
      pairedLineId: undefined,
      days: undefined,
    };
  });
}

/** Build study periods, deriving them from the lines when possible. */
function buildStudyPeriods(
  processedLines: LineDefinition[],
  gridData: any,
  settings: any
): StudyPeriodDefinition[] {
  let periods: any[] =
    gridData.study_periods ||
    settings.study_periods || [
      { id: 'semester1', name: 'Semester 1', lineIds: ['A', 'B', 'C'] },
      { id: 'semester2', name: 'Semester 2', lineIds: ['D', 'E', 'F'] },
    ];

  if (processedLines.length > 0) {
    const semester1Lines = processedLines.filter((l) => l.studyPeriod === 'semester1').map((l) => l.id);
    const semester2Lines = processedLines.filter((l) => l.studyPeriod === 'semester2').map((l) => l.id);

    if (semester1Lines.length === 0 && semester2Lines.length === 0) {
      const midPoint = Math.ceil(processedLines.length / 2);
      periods = [
        { id: 'semester1', name: 'Semester 1', lineIds: processedLines.slice(0, midPoint).map((l) => l.id) },
        { id: 'semester2', name: 'Semester 2', lineIds: processedLines.slice(midPoint).map((l) => l.id) },
      ];
    } else {
      periods = [
        { id: 'semester1', name: 'Semester 1', lineIds: semester1Lines },
        { id: 'semester2', name: 'Semester 2', lineIds: semester2Lines },
      ];
    }
  }

  // Carry both `lineIds` and `lines` populated identically so consumers reading
  // either field keep working (the two names drifted historically).
  return periods.map((p) => {
    const ids: string[] = p.lineIds || p.lines || [];
    return { id: p.id, name: p.name, lineIds: ids, lines: ids } as StudyPeriodDefinition;
  });
}

/**
 * Pick the class list to read from, preferring the first source that actually
 * holds classes.
 *
 * A grid's classes live in two places that are meant to stay in step:
 * `grid_data.classes` and the `classes` column. They can legitimately diverge —
 * CloneGridsToYear seeds `grid_data.classes` with `[]` for an empty template,
 * and older writers touched only one of the two. A plain `a || b` chain stops on
 * the empty array (truthy in JS), so a cloned grid would report zero classes
 * forever no matter what was written to the column. Choosing the first NON-EMPTY
 * source makes both layouts read correctly.
 */
function pickClasses(...sources: any[]): any[] {
  for (const source of sources) {
    if (Array.isArray(source) && source.length > 0) return source;
  }
  return [];
}

/**
 * Transform a raw `grids` row into a StaffAllocationGrid. `gridType` is NOT set
 * here — MultiGridContext augments the result with the joined grid_types record,
 * since only it loads grid types.
 */
export function transformGridRow(dbGrid: any): StaffAllocationGrid {
  const gridData = dbGrid.grid_data || {};
  const settings = dbGrid.settings || {};
  const metadata = dbGrid.metadata || {};

  const classes = pickClasses(gridData.classes, dbGrid.allocations, dbGrid.classes);
  const processedLines = normaliseLines(gridData, settings, metadata, classes);
  const semesters = buildStudyPeriods(processedLines, gridData, settings);

  return {
    id: dbGrid.id,
    name: dbGrid.name || 'Untitled Grid',
    description: dbGrid.description || '',
    classes,
    lines: processedLines,
    semesters,
    curriculumAreas: gridData.curriculum_areas || metadata.curriculumAreas || dbGrid.curriculum_areas || [],
    defaultClassMinutes: gridData.default_class_minutes || settings.default_class_minutes || 70,
    isActive: dbGrid.is_active !== undefined ? dbGrid.is_active : true,
    year: dbGrid.academic_year,
    term: dbGrid.term,
    createdAt: dbGrid.created_at ? new Date(dbGrid.created_at) : undefined,
    updatedAt: dbGrid.updated_at
      ? new Date(dbGrid.updated_at)
      : dbGrid.created_at
      ? new Date(dbGrid.created_at)
      : undefined,
  } as StaffAllocationGrid;
}
